import { Card, FormField, FormFieldRow, Heading, ReadOnlyField } from '@msaf/core-react'
import { FractionTypeCardViewProps, FractionTypeDescriptors, FractionTypeKeys, FractionTypes } from './lithology-types'
import { useMemo } from 'react'

export default function FractionCard({ fractionType, stratum, isLoading }: FractionTypeCardViewProps) {
  const getFieldValue = (fractionType: FractionTypes, fractionDescriptor: FractionTypeDescriptors) => {
    const key = `${fractionType}${fractionDescriptor}`
    if (key in stratum) {
      return stratum[key as keyof typeof stratum]
    }

    return ''
  }

  const showFracturingField = useMemo(() => {
    return fractionType === FractionTypeKeys.MAJOR || fractionType === FractionTypeKeys.SECONDARY
  }, [fractionType])

  return (
    <Card>
      <Heading level={4}>{`${fractionType.charAt(0).toUpperCase()}${fractionType.slice(1)} Fraction`}</Heading>
      <FormFieldRow>
        <FormField isSkeleton={isLoading} labelId={`${fractionType}FractionType`} label='Type'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy={`${fractionType}FractionType`}
            // https://smartlogic.io/blog/accessing-object-attributes-based-on-a-variable-in-typescript/
            // Alternatively value={stratum[`${fractionType}Fraction` as WellStratumTextKeysView]}
            value={
              `${fractionType}Fraction` in stratum ? stratum[`${fractionType}Fraction` as keyof typeof stratum] : ''
            }
          />
        </FormField>
      </FormFieldRow>

      <FormFieldRow>
        <FormField isSkeleton={isLoading} labelId={`${fractionType}FractionGrainsize`} label='Grain size'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy={`${fractionType}FractionGrainsize`}
            value={getFieldValue(fractionType, FractionTypeDescriptors.GRAINSIZE)}
          />
        </FormField>

        <FormField isSkeleton={isLoading} labelId={`${fractionType}FractionRockStrength`} label='Rock strength'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy={`${fractionType}FractionRockStrength`}
            value={getFieldValue(fractionType, FractionTypeDescriptors.ROCKSTRENGTH)}
          />
        </FormField>
      </FormFieldRow>

      <FormFieldRow>
        <FormField isSkeleton={isLoading} labelId={`${fractionType}FractionSorting`} label='Sorting'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy={`${fractionType}FractionSorting`}
            value={getFieldValue(fractionType, FractionTypeDescriptors.SORTING)}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId={`${fractionType}FractionWeathering`} label='Weathering'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy={`${fractionType}FractionWeathering`}
            value={getFieldValue(fractionType, FractionTypeDescriptors.WEATHERING)}
          />
        </FormField>
      </FormFieldRow>

      <FormFieldRow>
        <FormField isSkeleton={isLoading} labelId={`${fractionType}FractionRounding`} label='Rounding'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy={`${fractionType}FractionRounding`}
            value={getFieldValue(fractionType, FractionTypeDescriptors.ROUNDING)}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId={`${fractionType}FractionPlasticity`} label='Plasticity'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy={`${fractionType}FractionPlasticity`}
            value={getFieldValue(fractionType, FractionTypeDescriptors.PLASTICITY)}
          />
        </FormField>
      </FormFieldRow>

      {showFracturingField && (
        <FormFieldRow>
          <FormField isSkeleton={isLoading} labelId={`${fractionType}FractionFracturing`} label='Fracturing'>
            <ReadOnlyField
              isSkeleton={isLoading}
              labelledBy={`${fractionType}FractionFracturing`}
              value={getFieldValue(fractionType, FractionTypeDescriptors.FRACTURING)}
            />
          </FormField>
        </FormFieldRow>
      )}
    </Card>
  )
}
